/**
 * 窗屏內容發布——把已定案的景點打包成一版送上船。
 *
 * ## 兩層發布，這裡是第二層
 *
 *   景點的「發布」   → `is_published = true`（這個景點的內容定案了）
 *   窗屏內容發布     → 把**所有**已定案的景點打包成一版（`20260818_1`）  ← 這一頁
 *
 * 為什麼不是「改一個景點就自動跳一版」：發布是全域打包，A 景點存檔時 B 景點可能
 * 正編到一半，自動跳版會把 B 的中間狀態一起送上船；而船機只在靠港時拉更新，一堆
 * 沒人看過的中間版本只會讓差異計算失去意義。所以批次是對的——但要有人按得到。
 *
 * ## 為什麼發布前要先看一眼
 *
 * 發布**不可逆**（`release_manifests` 是 append-only，發錯只能再發一版蓋過去），
 * 而最容易踩到的是「以為會包進去、其實不會」：有窗屏編號但還是草稿的景點會被
 * 靜默略過，船上就少那一個景點，直到乘客站在玻璃前才看得出來。所以按鈕上方先把
 * 「這一版會包含誰、誰不會進去」攤開。
 *
 * 資料全部來自既有端點（景點清單、船機設定檔），沒有新增後端。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import {
  listDisplayConfigFiles,
  listSpots,
  publishDisplayRelease,
  type DisplayPublishResult,
  type Spot,
} from '../lib/api-client';

/** 有窗屏編號才會被打包；沒有編號的景點與窗屏無關（例如「包船」）。 */
function hasRoi(spot: Spot): boolean {
  return spot.roi_id !== null && spot.roi_id !== undefined;
}

function spotName(spot: Spot): string {
  return spot.name_i18n?.['zh-TW'] ?? spot.spot_code;
}

export function DisplayPublishPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const spotsQuery = useQuery({ queryKey: ['spots.list'], queryFn: () => listSpots() });
  const filesQuery = useQuery({
    queryKey: ['display.config-files'],
    queryFn: listDisplayConfigFiles,
  });

  const spots = spotsQuery.data ?? [];
  const included = spots.filter((s) => hasRoi(s) && s.is_published && !s.deleted_at);
  const excluded = spots.filter((s) => hasRoi(s) && !s.is_published && !s.deleted_at);

  /** 設定檔的提醒；留空的檔不會進發布包，那也要講。 */
  const files = filesQuery.data ?? [];
  const fileWarnings = files.flatMap((f) => f.warnings.map((w) => `${f.filename}：${w}`));
  const emptyFiles = files.filter((f) => f.line_count === 0).map((f) => f.filename);

  const publish = useMutation({
    mutationFn: publishDisplayRelease,
    onSuccess: () => {
      // 看板與模擬頁的答案都跟著這一版變了
      void qc.invalidateQueries({ queryKey: ['release-manifests'] });
    },
  });

  const confirmAndPublish = (): void => {
    const lines = [
      `確定發布？這一版會包含 ${included.length} 個景點：`,
      included.map((s) => `roi ${s.roi_id ?? '?'} ${spotName(s)}`).join('\n'),
      excluded.length > 0
        ? `\n以下 ${excluded.length} 個景點有窗屏編號但還是草稿，不會進這一版：\n` +
          excluded.map((s) => `roi ${s.roi_id ?? '?'} ${spotName(s)}`).join('\n')
        : '',
      '\n發布後無法刪除該版本（只能再發一版蓋過去）。',
    ];
    if (window.confirm(lines.filter(Boolean).join('\n'))) publish.mutate();
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">窗屏內容發布</h1>
      </div>

      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 6,
          padding: 12,
          margin: '12px 0',
          fontSize: 13,
          maxWidth: 1000,
        }}
      >
        把<strong>已發布且已設定窗屏編號</strong>的景點展開成船上的檔案樹 （
        <code>
          assets/{'{roi}'}/{'{lang}'}/…
        </code>
        ）、產生版本碼 <code>yyyymmdd_當日第幾版</code>、簽章後立即切成 active。船機下次靠港來問{' '}
        <code>/api/cms/update</code> 時拿到的就是這一版。
        <br />
        景點編輯<strong>不會</strong>
        自動跳版：發布是全域打包，自動跳版會把別人編到一半的景點一起送上船。
      </div>

      {(spotsQuery.error ?? filesQuery.error) && (
        <div className="error-banner">
          {((spotsQuery.error ?? filesQuery.error) as Error).message}
        </div>
      )}

      <section style={cardStyle}>
        <h2 style={{ margin: '0 0 8px', fontSize: 15 }}>這一版會包含什麼</h2>

        {spotsQuery.isLoading && <div style={{ fontSize: 13 }}>載入中…</div>}

        <SpotList
          title={`納入這一版（${included.length}）`}
          tone="ok"
          spots={included}
          empty="沒有任何已發布且有窗屏編號的景點——現在發布會被後端擋下（DISPLAY_PUBLISH_NO_SPOTS）。"
          onOpen={(id) => navigate(`/spots/${id}`)}
        />

        {excluded.length > 0 && (
          <SpotList
            title={`有窗屏編號但還是草稿，不會進去（${excluded.length}）`}
            tone="warn"
            spots={excluded}
            empty=""
            onOpen={(id) => navigate(`/spots/${id}`)}
            note="船上會直接少這些景點。要它們上船，請到景點頁按「發布」。"
          />
        )}
      </section>

      <section style={cardStyle}>
        <h2 style={{ margin: '0 0 8px', fontSize: 15 }}>設定檔</h2>
        {filesQuery.isLoading && <div style={{ fontSize: 13 }}>載入中…</div>}
        {emptyFiles.length > 0 && (
          <div style={warnBoxStyle}>
            ⚠ 這些檔是空的，<strong>不會寫進發布包</strong>：{emptyFiles.join('、')}
            。船機缺設定檔的行為由韌體決定，請確認是刻意留空。
          </div>
        )}
        {fileWarnings.map((w) => (
          <div key={w} style={warnBoxStyle}>
            ⚠ {w}
          </div>
        ))}
        {!filesQuery.isLoading && emptyFiles.length === 0 && fileWarnings.length === 0 && (
          <div style={{ fontSize: 13, color: '#6b7280' }}>沒有需要注意的事項。</div>
        )}
      </section>

      <section style={cardStyle}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="primary"
            onClick={confirmAndPublish}
            disabled={publish.isPending || included.length === 0}
          >
            {publish.isPending ? '發布中…' : '發布新版本'}
          </Button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            發布後<strong>無法刪除</strong>該版本（append-only），只能再發一版蓋過去。
          </span>
        </div>
        {publish.error && (
          <div className="error-banner" style={{ marginTop: 8 }}>
            {(publish.error as Error).message}
          </div>
        )}
        {publish.data && <PublishResultCard result={publish.data} onSimulate={navigate} />}
      </section>
    </div>
  );
}

function SpotList({
  title,
  tone,
  spots,
  empty,
  note,
  onOpen,
}: {
  title: string;
  tone: 'ok' | 'warn';
  spots: readonly Spot[];
  empty: string;
  note?: string;
  onOpen: (id: string) => void;
}): JSX.Element {
  return (
    <div style={{ margin: '8px 0' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: tone === 'ok' ? '#047857' : '#b45309' }}>
        {title}
      </div>
      {note && <div style={{ fontSize: 12, color: '#6b7280', margin: '2px 0' }}>{note}</div>}
      {spots.length === 0 && empty !== '' && (
        <div style={{ fontSize: 12, color: '#b45309', margin: '4px 0' }}>{empty}</div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
        {[...spots]
          .sort((a, b) => (a.roi_id ?? 0) - (b.roi_id ?? 0))
          .map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onOpen(s.id)}
              title={s.spot_code}
              style={{
                fontSize: 12,
                padding: '3px 8px',
                borderRadius: 10,
                cursor: 'pointer',
                background: tone === 'ok' ? '#ecfdf5' : '#fffbeb',
                color: tone === 'ok' ? '#047857' : '#92400e',
                border: `1px solid ${tone === 'ok' ? '#a7f3d0' : '#fcd34d'}`,
              }}
            >
              roi {s.roi_id} · {spotName(s)}
            </button>
          ))}
      </div>
    </div>
  );
}

function PublishResultCard({
  result,
  onSimulate,
}: {
  result: DisplayPublishResult;
  onSimulate: (to: string) => void;
}): JSX.Element {
  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        background: '#ecfdf5',
        border: '1px solid #6ee7b7',
        borderRadius: 6,
        fontSize: 13,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 600, color: '#047857' }}>
        ✓ 已發布 {result.version}
      </div>
      <div style={{ margin: '6px 0', color: '#374151' }}>
        {result.attraction_count} 個景點、{result.file_count} 個檔案 · release_id{' '}
        <code>{result.release_id}</code>
      </div>

      {result.warnings.length > 0 && (
        <div style={{ margin: '8px 0' }}>
          {result.warnings.map((w) => (
            <div key={w} style={warnBoxStyle}>
              ⚠ {w}
            </div>
          ))}
        </div>
      )}

      {/*
        發完最該做的一件事是驗證，而驗證的入口在另一頁。不給連結的話，這一版到底
        長什麼樣要自己想辦法找回來——版本碼也只在這張卡片上出現過一次。
      */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        <Button variant="secondary" onClick={() => onSimulate('/display-update-simulation')}>
          🛰️ 到船機更新模擬驗證（版本碼 {result.version}）
        </Button>
        <Button variant="ghost" onClick={() => onSimulate('/release-manifests')}>
          看 Release Manifest
        </Button>
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  padding: 16,
  margin: '12px 0',
  maxWidth: 1000,
};

const warnBoxStyle: React.CSSProperties = {
  background: '#fffbeb',
  border: '1px solid #fcd34d',
  borderRadius: 4,
  padding: 8,
  fontSize: 12,
  marginBottom: 6,
};
